/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useId, useState } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import DeleteIcon from '@atlaskit/icon/core/delete';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const styles = cssMap({
	queueContainer: {
		marginBlockStart: token('space.200'),
		marginBlockEnd: token('space.negative.050'),
		marginInlineStart: token('space.250'),
		marginInlineEnd: token('space.250'),
		position: 'relative',
		backgroundColor: token('elevation.surface.sunken'),
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.xlarge'),
		borderTopWidth: token('border.width'),
		borderRightWidth: token('border.width'),
		borderBottomWidth: 0,
		borderLeftWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		paddingBlockStart: token('space.075'),
		paddingBlockEnd: token('space.075'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
		overflow: 'hidden',
	},
	headerButton: {
		cursor: 'pointer',
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		paddingBlockStart: token('space.0'),
		paddingBlockEnd: token('space.0'),
		paddingInlineStart: token('space.0'),
		paddingInlineEnd: token('space.0'),
		borderWidth: 0,
		backgroundColor: token('elevation.surface.sunken'),
		textAlign: 'left',
	},
	messageList: {
		paddingBlockStart: token('space.050'),
	},
	messageRow: {
		paddingBlockStart: token('space.025'),
		paddingBlockEnd: token('space.025'),
	},
	previewText: {
		flexGrow: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		minWidth: 0,
	},
});

export type QueuedMessageItem = {
	id: number;
	preview: string;
	onRemove: () => void;
};

type QueuedMessageRowProps = {
	item: QueuedMessageItem;
	preview: string;
	removeLabel: string;
};

const QueuedMessageRow = ({
	item,
	preview,
	removeLabel,
}: QueuedMessageRowProps): React.JSX.Element => {
	return (
		<Box xcss={styles.messageRow}>
			<Inline alignBlock="center" space="space.075" spread="space-between">
				<Box xcss={styles.previewText}>
					<Text size="small" color="color.text.subtle">
						{preview}
					</Text>
				</Box>
				<IconButton
					icon={(iconProps) => (
						<DeleteIcon {...iconProps} label="" color={token('color.icon.subtle')} size="small" />
					)}
					label={removeLabel}
					appearance="subtle"
					spacing="compact"
					onClick={item.onRemove}
				/>
			</Inline>
		</Box>
	);
};

type QueuedMessageBannerProps = {
	items: QueuedMessageItem[];
};

export const QueuedMessageBanner = ({ items }: QueuedMessageBannerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(true);
	const messageListId = useId();
	const emptyPreviewLabel = formatMessage(messages.queuedMessageEmptyPreview);

	return (
		<Box xcss={styles.queueContainer}>
			<Pressable
				xcss={styles.headerButton}
				onClick={() => setIsExpanded((prev) => !prev)}
				aria-expanded={isExpanded}
				aria-controls={messageListId}
			>
				{isExpanded ? (
					<ChevronDownIcon label="" color={token('color.icon.subtle')} size="small" />
				) : (
					<ChevronUpIcon label="" color={token('color.icon.subtle')} size="small" />
				)}
				<Text size="small" color="color.text.subtlest" weight="semibold">
					{formatMessage(messages.queuedHeaderLabel, { count: items.length })}
				</Text>
			</Pressable>
			{isExpanded && (
				<Box id={messageListId} xcss={styles.messageList}>
					<Stack space="space.0">
						{items.map((item) => {
							const preview = item.preview || emptyPreviewLabel;
							return (
								<QueuedMessageRow
									key={item.id}
									item={item}
									preview={preview}
									removeLabel={formatMessage(messages.queuedMessageRemoveLabel, {
										preview,
									})}
								/>
							);
						})}
					</Stack>
				</Box>
			)}
		</Box>
	);
};
